import { toRef, computed, Ref } from "vue";
import { AdNetwork } from "@/components/Core/ActionBar/formats";
import {
  RecommendationStatus,
  RECOMMENDATION_STATUSES,
} from "@/services/recommendations/recommendations";
import {
  AvailablePresets,
  COUNTRIES,
  CountryDataFilter,
  DateRangeSelection,
  DateTimeISODateRangeWithPresetLabel,
  payloadToQueryParams,
  SEARCH_TERM_TYPES,
  FYR_FIRST_DATA_YEAR,
} from "@/services/data_filters";
import { useEventListener, useLocalStorage } from "@vueuse/core";
import { SearchTermDataFilter } from "@/services/data_filters";
import { DateRangePresets } from "@/components/Core/ActionBar/formats";
import {
  Resource,
  ResourceChannel,
  useResourcesQuery,
} from "@/services/resources";
import * as lodash from "lodash-es";
import {
  COMPETITOR_POSTS_SORT_METRICS,
  CompetitorPostSortKey,
} from "@/services/competitor_monitoring";
import { defineStore } from "pinia";
import {
  MarketingMetricSetName,
  MARKETING_METRIC_SET_NAMES,
} from "@/views/MarketingPerformance";
import {
  ANALYTICS_4_METRIC_SETS,
  ANALYTICS_4_METRIC_SET_NAMES,
  Analytics4Metric,
  Analytics4PageOrdering,
} from "@/services/website_performance_ga4";
import { MatomoPageOrdering } from "@/services/website_performance_matomo";
import { MetricFilterSet } from "@/services/metrics";
import { isoStringDayDifference } from "@/utils/dates";
import { FeedItemType } from "@/services/feed";
import { useRouter } from "vue-router";
import { ChangelogEntryType } from "@/services/changelog";
import { Organization } from "@/services/organization";
import { MarketingTableMetricKey } from "@/services/marketing";
import {
  SocialMediaPostSortKey,
  socialMediaOverviewPostSortMetrics,
  socialMediaPostSortKeys,
} from "@/services/social_media";
import { isCalculatedMetric } from "@/services/calculated_metrics";
import { EcommerceDatapointMetric } from "@/services/ecommerce";
import {
  OrganicSearchTermMetric,
  SearchTermMetric,
} from "@/services/search_terms";
import { DateTime } from "luxon";

const defaultPresetKey = "last30days";

const availableCountryDataTypes = COUNTRIES;
const availableSearchTermDataTypes = SEARCH_TERM_TYPES;
const availableRecommendationStatuses = RECOMMENDATION_STATUSES;
const availableSocialMediaPostsSortMetrics = socialMediaPostSortKeys;
const availableCompetitorPostsSortMetrics = COMPETITOR_POSTS_SORT_METRICS;
const availableMarketingMetricSets = MARKETING_METRIC_SET_NAMES;
const availableWebsiteAnalyticsMetricSets = ANALYTICS_4_METRIC_SET_NAMES;

export const defaultIncludedCheckboxFilters = {
  includedResources: [] as number[],
  includedTags: [] as number[],
  excludedTags: [] as number[],
  includedChannels: [] as ResourceChannel[],
  includedAdNetworks: [] as AdNetwork[],
  includedConversionGroups: [] as number[],
  includedConversions: [] as number[],
  includedGa4Conversions: [] as number[],
  includedUsers: [] as number[],
  includedFeedItemTypes: [] as FeedItemType[],
  includedCompetitors: [] as (number | null)[],
  includedDomains: [] as string[],
  includedChangelogEntryTypes: [] as ChangelogEntryType[],
  checkedEcommerceBrands: [] as string[],
  checkedEcommerceCategories: [] as string[],
  checkedShareOfSearchCountries: [] as number[],
  checkedShareOfSearchCategories: [] as number[],
  checkedShareOfSearchKeywords: [] as number[],
};
export type IncludedCheckboxFilterKeys =
  keyof typeof defaultIncludedCheckboxFilters;
export type IncludedCheckboxFilterValues =
  (typeof defaultIncludedCheckboxFilters)[IncludedCheckboxFilterKeys];

export const defaultAvailableCheckboxFilters = {
  availableChannels: [] as ResourceChannel[],
};
export const defaultSearchFilters = {
  socialMediaSearchInput: "",
  competitorMonitorSearchInput: "",
  feedItemSearchInput: "",
  recommendationSearchInput: "",
};
export const defaultListOptionFilters = {
  countryDataType: "total" as CountryDataFilter,
  searchTermDataType: "total" as SearchTermDataFilter,
  recommendationStatus: RecommendationStatus.OPEN as RecommendationStatus,
  // Posts page: which metric the post list is ranked by.
  socialMediaSortByMetric: "publishedAt" as SocialMediaPostSortKey,
  competitorMonitorSortByMetric: "publishedAt" as CompetitorPostSortKey,
  marketingMetricSet: "performance" as MarketingMetricSetName,
  websiteAnalyticsMetricSet: "default" as keyof typeof ANALYTICS_4_METRIC_SETS,
  yearFilter: String(DateTime.local().year),
  mpAdsSortByMetric: "impressions" as string,
  dashboardSortBy: "lastEdited" as DashboardSortBy,
  // AI Visibility view selectors (pk-as-string; "-1" = All areas / You).
  aiVisibilityArea: "-1" as string,
  // Brand focus: "all" = comparative (pie), "-1" = You, else a competitor pk.
  aiVisibilityBrand: "all" as string,
  // Engine lens ("" = all engines, else a VisibilityProvider value).
  aiVisibilityProvider: "" as string,
};

export type DashboardSortBy = "lastEdited" | "created" | "alphabetical";
export const DASHBOARD_SORT_OPTIONS: readonly DashboardSortBy[] = [
  "lastEdited",
  "created",
  "alphabetical",
] as const;
// Allowed aiVisibilityProvider values ("" = all engines). Clamps untrusted
// share-URL values to a known engine so they can't poison filter queries.
// MUST stay in sync with VisibilityProvider / VISIBILITY_PROVIDER_LABELS in
// services/ai_visibility.ts — we can't import them here because ai_visibility.ts
// imports this store (a direct import would create a circular dependency), so a
// new engine added there must be mirrored here.
const AI_VISIBILITY_PROVIDER_VALUES = new Set([
  "",
  "openai",
  "anthropic",
  "perplexity",
  "google",
]);

// A valid AI-Visibility area/brand selector: "-1" (All / You) or a positive
// integer pk as a string. Clamps junk values from a shared URL so they can't
// reach the query as a bogus filter.
function isVisibilityPkSelector(value: unknown): boolean {
  return (
    typeof value === "string" && (value === "-1" || /^[1-9]\d*$/.test(value))
  );
}

export const defaultBooleanFilters = {
  // Default ON: split each account into its own series rather than combining
  // same-channel accounts. Users can toggle it off (the choice is shareable and
  // session-scoped). See "Split accounts" toggle in the Ad Performance ActionBar.
  separateResources: true,
  metricTotals: false,
  includeAllEvents: false,
  includeWebsiteRecommendations: false,
};

export const defaultMarketingPageLevelFilters = {
  mpFilterCriteria: [] as MetricFilterSet[],
  mpSelectedColumns: [] as MarketingTableMetricKey[],
  mpAdsViewMode: "list" as "list" | "cards",
};

export const defaultSearchTermPageLevelFilters = {
  stFilterCriteria: [] as MetricFilterSet[],
  stSelectedColumns: [] as SearchTermMetric[],
};

export const defaultSearchTermDetailPageLevelFilters = {
  stDetailPageFilterCriteria: [] as MetricFilterSet[],
  stDetailSearchTermFilterCriteria: [] as MetricFilterSet[],
  stDetailPageSelectedColumns: [] as OrganicSearchTermMetric[],
};

export const defaultWaPagesPageLevelFilters = {
  waPagesSelectedColumns: [] as Analytics4Metric[],
  waPagesSortMetric: "sessions" as Analytics4PageOrdering | MatomoPageOrdering,
};
export const defaultWaAcquisitionPageLevelFilters = {
  waAcqSelectedColumns: [] as Analytics4Metric[],
};
export const defaultWaConversionsPageLevelFilters = {
  waConvTableSelectedColumns: [] as string[],
};

export type SocialMediaMetricView = "total" | "organic" | "paid";
export const defaultSocialMediaOverviewPageLevelFilters = {
  smFilterCriteria: [] as MetricFilterSet[],
  smViewMode: "cards" as "list" | "cards",
  // Posts page: filters posts by sponsored/organic. Kept page-specific so the
  // selection doesn't leak into the account-summary "Data type" split below.
  smMetricView: "total" as SocialMediaMetricView,
  // Account-summary page: drives the organic/paid split on the topline stat
  // cards + charts. Separate key from smMetricView so the two pages don't share
  // a selection (CLAUDE.md: don't contaminate global state across pages).
  smOverviewMetricView: "total" as SocialMediaMetricView,
  // Account-summary "Top posts": separate key from socialMediaSortByMetric
  // because that panel only offers metrics common to pages and posts, so a
  // Posts selection like videoWatchTime isn't in its list — sharing one key
  // left the button labelled with a metric it couldn't mark as selected.
  // Defaults to a metric rather than publishedAt: that panel ranks "top
  // performing" posts, and publishedAt is a sort key but not a post metric, so
  // it could never appear in the panel's own options either.
  smOverviewSortByMetric: "impressions" as SocialMediaPostSortKey,
};

export const defaultEcommercePageLevelFilters = {
  ecProductTableFilterCriteria: [] as MetricFilterSet[],
  ecSourceMediumTableFilterCriteria: [] as MetricFilterSet[],
  ecSourceMediumTableSelectedColumns: [] as EcommerceDatapointMetric[],
};
export const defaultCompMonOverviewPageLevelFilters = {
  cmoPostsFilterCriteria: [] as MetricFilterSet[],
  cmpViewMode: "cards" as "list" | "cards",
};

export const defaultPageLevelFilters = {
  ...defaultMarketingPageLevelFilters,
  ...defaultSearchTermPageLevelFilters,
  ...defaultSearchTermDetailPageLevelFilters,
  ...defaultWaPagesPageLevelFilters,
  ...defaultWaAcquisitionPageLevelFilters,
  ...defaultWaConversionsPageLevelFilters,
  ...defaultSocialMediaOverviewPageLevelFilters,
  ...defaultEcommercePageLevelFilters,
  ...defaultCompMonOverviewPageLevelFilters,
};

// Stricter than isCalculatedMetric()'s prefix test: a sort key that only
// starts with the prefix ("calculatedMetricFOO" from a hand-edited share URL)
// would otherwise survive the clamp and reach the query as an unknown column.
// Existence is still not checked here — the store has no access to the loaded
// metric list; the dropdown's readiness-gated clamp handles a deleted one.
function isCalculatedMetricKey(metric: string): boolean {
  return /^calculatedMetric\d+$/.test(metric);
}

export const sharableFilters = {
  ...defaultIncludedCheckboxFilters,
  ...defaultSearchFilters,
  ...defaultListOptionFilters,
  ...defaultBooleanFilters,
  ...defaultPageLevelFilters,
};

// deep copies for the store, to avoid changing the defaults
const sharableFiltersCopy = lodash.cloneDeep(sharableFilters);
const defaultAvailableCheckboxFiltersCopy = lodash.cloneDeep(
  defaultAvailableCheckboxFilters,
);
// const defaultIncludedCheckboxFiltersCopy lodash.cloneDeep(defaultIncludedCheckboxFilters);
// const defaultSearchFiltersCopy lodash.cloneDeep(defaultSearchFilters);
// const defaultListOptionFiltersCopy lodash.cloneDeep(defaultListOptionFilters);
// const defaultBooleanFiltersCopy lodash.cloneDeep(defaultBooleanFilters);

export const useDataFiltersStore = defineStore("dataFilters", {
  state: () => {
    const availablePresets = DateRangePresets();
    const defaultPreset = availablePresets[defaultPresetKey];
    return {
      ...sharableFiltersCopy,
      ...defaultAvailableCheckboxFiltersCopy,
      availablePresets,
      temporalFilter: useLocalStorage("temporal_filter", {
        // TODO: Typeguard
        selection: {
          preset: defaultPreset.label(),
          start: defaultPreset.range().start.toISODate(),
          end: defaultPreset.range().end.toISODate(),
        },
      } as {
        selection: DateTimeISODateRangeWithPresetLabel;
        comparison?: DateTimeISODateRangeWithPresetLabel;
      }),
      isTableComparisonEnabled: useLocalStorage(
        "isTableComparisonEnabled",
        true,
      ),
      singleResourceSelection: useLocalStorage(
        "singleResourceSelection",
        {} as Record<Organization["id"], Resource["pk"]>,
      ),
      fields: useLocalStorage<string[] | null>("fields", null),
    };
  },
  actions: {
    setTemporalFilter(filter: DateRangeSelection): void {
      const applying = Object.assign({}, filter);
      // Never allow future dates in the global temporal filter
      const today = DateTime.local().toISODate()!;
      if (applying.selection.end > today) {
        applying.selection = {
          ...applying.selection,
          start:
            applying.selection.start > today ? today : applying.selection.start,
          end: today,
        };
      }
      if (applying.comparison && applying.comparison.end > today) {
        applying.comparison = {
          ...applying.comparison,
          start:
            applying.comparison.start > today
              ? today
              : applying.comparison.start,
          end: today,
        };
      }
      this.temporalFilter = applying;
    },
    setTagFilter(included: number[], excluded: number[]): void {
      this.includedTags = included;
      this.excludedTags = excluded;
    },
    includeItem(
      item: Unpacked<IncludedCheckboxFilterValues>,
      array: IncludedCheckboxFilterKeys,
      include: boolean,
    ): void {
      if (include) {
        if (!this[array].includes(item as never)) {
          this[array] = [...this[array], item];
        }
      } else {
        this[array] = (this[array] as never[]).filter(
          (r: typeof item) => r !== item,
        );
      }
    },
    //** resets boolean toggles and string (single list option) filters to their default incase they have been set differently by a malicious share URL */
    typeGuardFilters(): void {
      // Validate temporal filter structure before clamping
      function isIsoDate(value: unknown): value is string {
        return typeof value === "string" && DateTime.fromISO(value).isValid;
      }

      const selection = this.temporalFilter?.selection;
      if (
        !selection ||
        typeof selection.preset !== "string" ||
        !isIsoDate(selection.start) ||
        !isIsoDate(selection.end)
      ) {
        const defaultPreset = this.availablePresets[defaultPresetKey];
        this.temporalFilter = {
          selection: {
            preset: defaultPreset.label(),
            start: defaultPreset.range().start.toISODate()!,
            end: defaultPreset.range().end.toISODate()!,
          },
        };
      }

      if (
        this.temporalFilter.selection.start > this.temporalFilter.selection.end
      ) {
        const defaultPreset = this.availablePresets[defaultPresetKey];
        this.temporalFilter = {
          selection: {
            preset: defaultPreset.label(),
            start: defaultPreset.range().start.toISODate()!,
            end: defaultPreset.range().end.toISODate()!,
          },
        };
      }

      const comparison = this.temporalFilter?.comparison;
      if (
        comparison &&
        (typeof comparison.preset !== "string" ||
          !isIsoDate(comparison.start) ||
          !isIsoDate(comparison.end))
      ) {
        this.temporalFilter = {
          ...this.temporalFilter,
          comparison: undefined,
        };
      }

      if (
        this.temporalFilter.comparison &&
        this.temporalFilter.comparison.start >
          this.temporalFilter.comparison.end
      ) {
        this.temporalFilter = {
          ...this.temporalFilter,
          comparison: undefined,
        };
      }

      // Clamp future dates in temporalFilter back to today
      const today = DateTime.local().toISODate()!;
      if (this.temporalFilter.selection.end > today) {
        this.temporalFilter = {
          ...this.temporalFilter,
          selection: {
            ...this.temporalFilter.selection,
            start:
              this.temporalFilter.selection.start > today
                ? today
                : this.temporalFilter.selection.start,
            end: today,
          },
        };
      }
      if (
        this.temporalFilter.comparison &&
        this.temporalFilter.comparison.end > today
      ) {
        this.temporalFilter = {
          ...this.temporalFilter,
          comparison: {
            ...this.temporalFilter.comparison,
            start:
              this.temporalFilter.comparison.start > today
                ? today
                : this.temporalFilter.comparison.start,
            end: today,
          },
        };
      }
      if (typeof this.separateResources !== "boolean") {
        this.separateResources = defaultBooleanFilters.separateResources;
      }
      if (typeof this.metricTotals !== "boolean") {
        this.metricTotals = false;
      }
      if (typeof this.includeAllEvents !== "boolean") {
        this.includeAllEvents = false;
      }
      if (typeof this.includeWebsiteRecommendations !== "boolean") {
        this.includeWebsiteRecommendations = false;
      }
      if (typeof this.recommendationSearchInput !== "string") {
        this.recommendationSearchInput = "";
      }
      if (!availableCountryDataTypes.includes(this.countryDataType)) {
        this.countryDataType = availableCountryDataTypes[0];
      }
      if (!availableSearchTermDataTypes.includes(this.searchTermDataType)) {
        this.searchTermDataType = availableSearchTermDataTypes[0];
      }
      if (
        !availableRecommendationStatuses.includes(this.recommendationStatus)
      ) {
        this.recommendationStatus = availableRecommendationStatuses[0];
      }
      if (
        !availableSocialMediaPostsSortMetrics.includes(
          this
            .socialMediaSortByMetric as (typeof availableSocialMediaPostsSortMetrics)[number],
        ) &&
        !isCalculatedMetricKey(this.socialMediaSortByMetric)
      ) {
        this.socialMediaSortByMetric = availableSocialMediaPostsSortMetrics[0];
      }
      if (
        !socialMediaOverviewPostSortMetrics.includes(
          this.smOverviewSortByMetric,
        ) &&
        !isCalculatedMetricKey(this.smOverviewSortByMetric)
      ) {
        // The panel's own default, not socialMediaPostSortKeys[0]: that is
        // publishedAt, which the "Top posts" panel never offers as an option.
        this.smOverviewSortByMetric =
          defaultSocialMediaOverviewPageLevelFilters.smOverviewSortByMetric;
      }
      if (
        !availableCompetitorPostsSortMetrics.includes(
          this.competitorMonitorSortByMetric,
        )
      ) {
        this.competitorMonitorSortByMetric =
          availableCompetitorPostsSortMetrics[0];
      }
      if (!availableMarketingMetricSets.includes(this.marketingMetricSet)) {
        this.marketingMetricSet = availableMarketingMetricSets[0];
      }
      if (
        !availableWebsiteAnalyticsMetricSets.includes(
          this.websiteAnalyticsMetricSet,
        )
      ) {
        this.websiteAnalyticsMetricSet = availableWebsiteAnalyticsMetricSets[0];
      }
      if (typeof this.yearFilter !== "string") {
        this.yearFilter = String(DateTime.local().year);
      } else {
        // Clamp, not just normalise: `DateTime.local(1999).year` is 1999, so a
        // share URL could put a year with no data into every year-scoped query.
        // Done here rather than on the dropdown so one check covers both
        // viewports and the `$patch` share-apply path, which no mounted
        // component can guard.
        const parsed = parseInt(this.yearFilter);
        const earliest = FYR_FIRST_DATA_YEAR;
        const latest = DateTime.local().year + 1;
        this.yearFilter = String(
          Number.isNaN(parsed)
            ? DateTime.local().year
            : Math.min(Math.max(parsed, earliest), latest),
        );
      }
      if (this.mpAdsViewMode !== "list" && this.mpAdsViewMode !== "cards") {
        this.mpAdsViewMode = "list";
      }
      if (this.smViewMode !== "list" && this.smViewMode !== "cards") {
        this.smViewMode = "cards";
      }
      if (
        this.smMetricView !== "total" &&
        this.smMetricView !== "organic" &&
        this.smMetricView !== "paid"
      ) {
        this.smMetricView = "total";
      }
      if (
        this.smOverviewMetricView !== "total" &&
        this.smOverviewMetricView !== "organic" &&
        this.smOverviewMetricView !== "paid"
      ) {
        this.smOverviewMetricView = "total";
      }
      if (this.cmpViewMode !== "list" && this.cmpViewMode !== "cards") {
        this.cmpViewMode = "cards";
      }
      if (
        !(DASHBOARD_SORT_OPTIONS as readonly string[]).includes(
          this.dashboardSortBy,
        )
      ) {
        this.dashboardSortBy = DASHBOARD_SORT_OPTIONS[0];
      }
      // AI Visibility selectors are pk-as-string. A malformed share URL must
      // not inject a non-string or a junk value: area accepts "-1" or a
      // positive-integer pk; brand also accepts "all" (comparative). Anything
      // else resets to the default so a bad ?aiVisibilityBrand=foo can't reach
      // the query as a bogus filter.
      if (!isVisibilityPkSelector(this.aiVisibilityArea)) {
        this.aiVisibilityArea = "-1";
      }
      if (
        this.aiVisibilityBrand !== "all" &&
        !isVisibilityPkSelector(this.aiVisibilityBrand)
      ) {
        this.aiVisibilityBrand = "all";
      }
      if (
        typeof this.aiVisibilityProvider !== "string" ||
        !AI_VISIBILITY_PROVIDER_VALUES.has(this.aiVisibilityProvider)
      ) {
        this.aiVisibilityProvider = "";
      }
    },
    sharableFilterIsDefaultValue(key: keyof typeof sharableFilters): boolean {
      return lodash.isEqual(this.$state[key], sharableFilters[key]);
    },
    setFilterToDefaultValue(key: keyof typeof sharableFilters): void {
      if (key in sharableFilters) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        this.$state[key] = sharableFilters[key];
      }
    },

    initializeDataFilters({
      availableChannels,
    }: {
      availableChannels?: readonly ResourceChannel[];
    } = {}): void {
      this.typeGuardFilters();
      if (availableChannels) {
        this.availableChannels = [...availableChannels];
        this.includedChannels = this.includedChannels.filter((r) =>
          availableChannels.includes(r),
        );
      }
    },
    resetSharableFilters(): void {
      this.$patch(
        lodash.cloneDeep(
          lodash.pick(
            sharableFilters,
            ...Object.keys(
              lodash.omit(
                sharableFilters, // don't reset page level filters, and list options except search term data type and the year
                ...Object.keys(defaultListOptionFilters),
                ...Object.keys(defaultPageLevelFilters),
              ),
            ),
            "searchTermDataType",
            "yearFilter",
          ),
        ),
      );
    },
    updateTemporalFilter(): void {
      // This code updates the daterange selection if a preset is selected but the localStorage date
      // selection doesn't match the preset's daterange. Basically, if a user selects "This month", and
      // is browsing the app at Jan 4th 23:59, then this code will make sure that if the user F5's
      // after the clock hits Jan 5th, then their new daterange will also include Jan 4th
      // TODO: Make presets react to the current date changing
      this.availablePresets = DateRangePresets();
      if (this.temporalFilter.selection.preset !== "Custom") {
        const matchingPresetKey = lodash.findKey(this.availablePresets, (e) => {
          return e.label() === this.temporalFilter.selection.preset;
        }) as keyof AvailablePresets;
        if (matchingPresetKey) {
          const { start, end } =
            this.availablePresets[matchingPresetKey].range();
          if (
            start.toISODate() !== this.temporalFilter.selection.start ||
            end.toISODate() !== this.temporalFilter.selection.end
          ) {
            const comparisonPresets =
              this.availablePresets[matchingPresetKey].comparisonPresets;
            const matchingComparisonPreset = Object.values(
              comparisonPresets,
            ).find((b) => {
              return b.label === this.temporalFilter.comparison?.preset;
            });
            if (
              this.temporalFilter.comparison &&
              this.temporalFilter.comparison?.preset === "Personalized"
            ) {
              this.setTemporalFilter({
                selection: {
                  preset: this.temporalFilter.selection.preset,
                  start: start.toISODate()!,
                  end: end.toISODate()!,
                },
                comparison: this.temporalFilter.comparison,
              });
            } else {
              this.setTemporalFilter({
                selection: {
                  preset: this.temporalFilter.selection.preset,
                  start: start.toISODate()!,
                  end: end.toISODate()!,
                },
                ...(matchingComparisonPreset && {
                  comparison: {
                    preset: matchingComparisonPreset.label,
                    start: matchingComparisonPreset.range().start.toISODate()!,
                    end: matchingComparisonPreset.range().end.toISODate()!,
                  },
                }),
              });
            }
          }
        }
      }
    },
  },
  getters: {
    temporalFilterSelectionInDays(): number {
      return isoStringDayDifference(
        this.temporalFilter.selection.start,
        this.temporalFilter.selection.end,
      );
    },
  },
});

const SHARE_PARAM_ALLOWED_KEYS = new Set([
  ...Object.keys(sharableFilters),
  "temporalFilter",
  "isTableComparisonEnabled",
]);

/**
 * Parse a `share` query parameter and apply it to the data filters store.
 * Resets all sharable filters to defaults first, then overlays the shared
 * values so the recipient sees exactly the sharer's view.
 */
export function applyShareParam(shareParam: string): void {
  let parsed: unknown;
  try {
    parsed = JSON.parse(shareParam);
  } catch {
    return;
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return;
  }

  const replacement = lodash.cloneDeep(sharableFilters) as Record<
    string,
    unknown
  >;

  for (const [key, value] of Object.entries(
    parsed as Record<string, unknown>,
  )) {
    if (SHARE_PARAM_ALLOWED_KEYS.has(key)) {
      replacement[key] = value;
    }
  }

  const store = useDataFiltersStore();
  store.$patch(replacement);
  store.typeGuardFilters();
}

function useFullDataFilters() {
  const dataFiltersStore = useDataFiltersStore();
  return {
    channel: {
      channel__in: computed(() => dataFiltersStore.includedChannels),
    },
    includedResources: {
      resource__in: computed(() => dataFiltersStore.includedResources),
    },
    selectionTemporalFilter: {
      selection_from: computed(
        () => dataFiltersStore.temporalFilter.selection.start,
      ),
      selection_to: computed(
        () => dataFiltersStore.temporalFilter.selection.end,
      ),
    },
    temporalFilter: {
      selection_from: computed(
        () => dataFiltersStore.temporalFilter.selection.start,
      ),
      selection_to: computed(
        () => dataFiltersStore.temporalFilter.selection.end,
      ),
      comparison_from: computed(
        () => dataFiltersStore.temporalFilter.comparison?.start ?? null,
      ),
      comparison_to: computed(
        () => dataFiltersStore.temporalFilter.comparison?.end ?? null,
      ),
    },
    includedTags: {
      tags__in: computed(() => dataFiltersStore.includedTags),
      tags_logic: computed(() => "OR" as "AND" | "OR"),
    },
    excludedTags: {
      not__tags__in: computed(() => dataFiltersStore.excludedTags),
    },
    subcategory: {
      subcategory__in: computed(() => dataFiltersStore.includedAdNetworks),
    },
    conversionGroup: {
      conversion_group__in: computed(
        () => dataFiltersStore.includedConversionGroups,
      ),
    },
    conversion: {
      conversion__in: computed(() => dataFiltersStore.includedConversions),
    },
    brands: { brand: computed(() => dataFiltersStore.checkedEcommerceBrands) },
    categories: {
      category: computed(() => dataFiltersStore.checkedEcommerceCategories),
    },
    domains: {
      domain__in: computed(() => dataFiltersStore.includedDomains),
    },
    separateResources: {
      separateResources: toRef(dataFiltersStore, "separateResources"),
    },
    includeAllEvents: {
      is_conversion_event: computed(() =>
        dataFiltersStore.includeAllEvents ? null : true,
      ),
    },
    includedUsers: {
      user__in: computed(() => dataFiltersStore.includedUsers),
      created_by__in: computed(() => dataFiltersStore.includedUsers),
    },
    includedFeedItemTypes: {
      item_type__in: computed(() => dataFiltersStore.includedFeedItemTypes),
    },
    feedItemSearchInput: {
      search: computed(() => dataFiltersStore.feedItemSearchInput),
    },
    shareOfSearchCompetitors: {
      competitorIds: computed(
        () =>
          dataFiltersStore.includedCompetitors.map((pk) => pk ?? "null") as (
            | number
            | null
          )[],
      ),
    },
    shareOfSearchCategories: {
      categories: toRef(dataFiltersStore, "checkedShareOfSearchCategories"),
    },
    shareOfSearchCountries: {
      countries: toRef(dataFiltersStore, "checkedShareOfSearchCountries"),
    },
    shareOfSearchKeywords: {
      keywords: toRef(dataFiltersStore, "checkedShareOfSearchKeywords"),
    },
    yearFilter: {
      year: computed(() => dataFiltersStore.yearFilter),
    },
    fieldsFilter: {
      fields: toRef(dataFiltersStore, "fields"),
    },
  };
}

// The reason for the complexity in this function is that it picks keys
// based on the outer keys in useFullDataFilters (e.g. includedTags),
// but it produces an object with keys based on the inner keys in
// useFullDataFilters (e.g. tags__in).
export function useDataFilters<
  K extends keyof ReturnType<typeof useFullDataFilters>,
>(
  keys: K[],
  // When false this function is essentially a no-op.
  // This is used to avoid including "global" (action bar) filters in certain cases,
  // such as dashboards.
  useGlobalFilters = true,
) {
  const fullDataFilters = useFullDataFilters();
  const dataFilters = Object.fromEntries(
    Object.values(lodash.pick(fullDataFilters, keys))
      .flatMap((obj) => Object.entries(obj as Record<string, Ref>))
      .map(([k, v]) => [k, computed(() => payloadToQueryParams(v.value))]),
  ) as UnionToIntersection<ReturnType<typeof useFullDataFilters>[K]>;
  if (useGlobalFilters) {
    return dataFilters;
  } else {
    return Object.fromEntries(
      //@ts-expect-error ...
      Object.keys(dataFilters).map((k) => [k, computed(() => null)]),
    ) as typeof dataFilters;
  }
}

export function useCalculatedResources(
  selectedResourceIds?: Ref<number[]>,
  useGlobalFilters = true,
): {
  resource__in: Ref<number[] | null>;
} {
  return selectedResourceIds
    ? {
        resource__in: computed(
          () =>
            payloadToQueryParams(selectedResourceIds.value) as number[] | null,
        ),
      }
    : useDataFilters(["includedResources"], useGlobalFilters);
}
export function useConversionGroups(
  conversionGroups?: Ref<number[]>,
  useGlobalFilters = true,
): {
  conversion_group__in: Ref<number[] | null>;
} {
  return conversionGroups
    ? {
        conversion_group__in: computed(
          () => payloadToQueryParams(conversionGroups.value) as number[] | null,
        ),
      }
    : useDataFilters(["conversionGroup"], useGlobalFilters);
}

export function useConversions(
  conversions?: Ref<number[]>,
  useGlobalFilters = true,
): {
  conversion__in: Ref<number[] | null>;
} {
  return conversions
    ? {
        conversion__in: computed(
          () => payloadToQueryParams(conversions.value) as number[] | null,
        ),
      }
    : useDataFilters(["conversion"], useGlobalFilters);
}

export function useMetricFilter(
  metrics?: Ref<string[]>,
  useGlobalFilters = true,
): {
  fields: Ref<string[] | null>;
} {
  return metrics
    ? {
        fields: computed(() => {
          const metricValues = metrics.value;
          if (!metricValues) return null;
          const convertedMetrics = metricValues.map((metric) => {
            if (isCalculatedMetric(metric)) {
              // Extract the ID from calculatedMetric123 and convert to calculated_metric_123
              return metric.replace(/^calculatedMetric/, "calculated_metric_");
            }
            return metric;
          });

          return payloadToQueryParams(convertedMetrics) as string[] | null;
        }),
      }
    : useDataFilters(["fieldsFilter"], useGlobalFilters);
}

export function useDetachedTemporalFilter(
  temporalFilter?: Ref<DateRangeSelection>,
  useGlobalFilters = true,
): {
  selection_from: Ref<string>;
  selection_to: Ref<string>;
  comparison_from: Ref<string | null>;
  comparison_to: Ref<string | null>;
} {
  return temporalFilter
    ? {
        selection_from: computed(() => temporalFilter.value.selection.start),
        selection_to: computed(() => temporalFilter.value.selection.end),
        comparison_from: computed(
          () => temporalFilter.value.comparison?.start ?? null,
        ),
        comparison_to: computed(
          () => temporalFilter.value.comparison?.end ?? null,
        ),
      }
    : useDataFilters(["temporalFilter"], useGlobalFilters);
}

export function useDetachedSelectionTemporalFilter(
  temporalFilter?: Ref<DateRangeSelection>,
  useGlobalFilters = true,
): {
  selection_from: Ref<string>;
  selection_to: Ref<string>;
} {
  return temporalFilter
    ? {
        selection_from: computed(() => temporalFilter.value.selection.start),
        selection_to: computed(() => temporalFilter.value.selection.end),
      }
    : useDataFilters(["selectionTemporalFilter"], useGlobalFilters);
}

export function useDetachedTagFilter(
  useGlobalFilters = true,
  includedTags?: Ref<number[]>,
  excludedTags?: Ref<number[]>,
  includedTagsLogic?: Ref<"AND" | "OR">,
): {
  tags__in: Ref<number[] | null>;
  not__tags__in: Ref<number[] | null>;
  tags_logic: Ref<"AND" | "OR">;
} {
  return includedTags
    ? {
        tags__in: computed(
          () => payloadToQueryParams(includedTags.value) as number[] | null,
        ),
        not__tags__in: computed(
          () =>
            payloadToQueryParams(excludedTags?.value ?? []) as number[] | null,
        ),
        tags_logic: computed(() => includedTagsLogic?.value ?? "OR"),
      }
    : useDataFilters(["includedTags", "excludedTags"], useGlobalFilters);
}

export function useShareOfSearchCategoriesFilter(categories?: Ref<number[]>): {
  categories: Ref<number[] | undefined>;
} {
  return categories
    ? {
        categories,
      }
    : useDataFilters(["shareOfSearchCategories"]);
}
export function useShareOfSearchCountriesFilter(countries?: Ref<number[]>): {
  countries: Ref<number[] | undefined>;
} {
  return countries
    ? {
        countries,
      }
    : useDataFilters(["shareOfSearchCountries"]);
}
export function useShareOfSearchKeywordsFilter(keywords?: Ref<number[]>): {
  keywords: Ref<number[] | undefined>;
} {
  return keywords
    ? {
        keywords,
      }
    : useDataFilters(["shareOfSearchKeywords"]);
}
export function useShareOfSearchCompetitorsFilter(
  competitorIds?: Ref<number[]>,
): {
  competitorIds: Ref<(number | null)[] | undefined>;
} {
  return competitorIds
    ? {
        competitorIds,
      }
    : useDataFilters(["shareOfSearchCompetitors"]);
}

export function useUpdateTemporalFilter() {
  const dataFiltersStore = useDataFiltersStore();

  useEventListener(window, "focus", dataFiltersStore.updateTemporalFilter);

  const router = useRouter();
  router.beforeEach(dataFiltersStore.updateTemporalFilter);
}

export function useAvailableResources() {
  const resources = useResourcesQuery();
  const dataFiltersStore = useDataFiltersStore();
  return computed(
    () =>
      resources?.data?.filter((resource) =>
        dataFiltersStore.availableChannels.includes(resource.channel),
      ) ?? [],
  );
}
